import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
    PulseCheck,
    PulseCheckDto,
    PulseInsight,
    PulseResponseView,
    PulseQueueItem,
    PulseRespondView,
    CreatePulseCheckDto,
    UpdatePulseCheckDto,
    SubmitPulseResponseDto,
    SharePulseInsightDto,
    ApiResponse,
    FormTemplateDefinition
} from '../../../shared/types';
import axiosService from '../services/axios.service';
import { useCrewModuleEnabled } from './useCrews';

// ============================================
// Query key factory
// ============================================
export const pulseKeys = {
    all: ['pulse'] as const,
    crew: (crewSlug: string) => [...pulseKeys.all, 'crew', crewSlug] as const,
    sessions: (crewSlug: string) => [...pulseKeys.crew(crewSlug), 'sessions'] as const,
    session: (crewSlug: string, sessionId: string) =>
        [...pulseKeys.sessions(crewSlug), sessionId] as const,
    queue: (crewSlug: string) => [...pulseKeys.crew(crewSlug), 'queue'] as const,
    insight: (crewSlug: string, sessionId: string) =>
        [...pulseKeys.session(crewSlug, sessionId), 'insight'] as const,
    responses: (crewSlug: string, sessionId: string) =>
        [...pulseKeys.session(crewSlug, sessionId), 'responses'] as const,
    respondView: (crewSlug: string, token: string) =>
        [...pulseKeys.crew(crewSlug), 'respond', token] as const,
    templates: (crewSlug: string) => [...pulseKeys.crew(crewSlug), 'templates'] as const
};

// ============================================
// API functions
// ============================================
const pulseApi = {
    // Templates
    getTemplates: async (crewSlug: string): Promise<FormTemplateDefinition[]> => {
        const { data } = await axiosService.get<ApiResponse<FormTemplateDefinition[]>>(
            `/c/${crewSlug}/pulse/templates`
        );
        return data.data ?? [];
    },

    promoteTemplate: async (crewSlug: string, templateId: string): Promise<void> => {
        await axiosService.post(`/c/${crewSlug}/pulse/templates/${templateId}/promote`);
    },

    updateTemplateHeaderImage: async (
        crewSlug: string,
        templateId: string,
        headerImageUrl: string | null
    ): Promise<FormTemplateDefinition> => {
        const { data } = await axiosService.patch<ApiResponse<FormTemplateDefinition>>(
            `/c/${crewSlug}/pulse/templates/${templateId}/header-image`,
            { headerImageUrl }
        );
        return data.data!;
    },

    // Sessions
    getSessions: async (crewSlug: string): Promise<PulseCheck[]> => {
        const { data } = await axiosService.get<ApiResponse<PulseCheck[]>>(
            `/c/${crewSlug}/pulse/sessions`
        );
        return data.data ?? [];
    },

    getSession: async (crewSlug: string, sessionId: string): Promise<PulseCheckDto | null> => {
        const { data } = await axiosService.get<ApiResponse<PulseCheckDto>>(
            `/c/${crewSlug}/pulse/sessions/${sessionId}`
        );
        return data.data ?? null;
    },

    getSessionResponses: async (
        crewSlug: string,
        sessionId: string
    ): Promise<PulseResponseView[]> => {
        const { data } = await axiosService.get<ApiResponse<PulseResponseView[]>>(
            `/c/${crewSlug}/pulse/sessions/${sessionId}/responses`
        );
        return data.data ?? [];
    },

    createSession: async (
        crewSlug: string,
        dto: CreatePulseCheckDto
    ): Promise<PulseCheck> => {
        const { data } = await axiosService.post<ApiResponse<PulseCheck>>(
            `/c/${crewSlug}/pulse/sessions`,
            dto
        );
        return data.data!;
    },

    updateSession: async (
        crewSlug: string,
        sessionId: string,
        dto: UpdatePulseCheckDto
    ): Promise<PulseCheck> => {
        const { data } = await axiosService.put<ApiResponse<PulseCheck>>(
            `/c/${crewSlug}/pulse/sessions/${sessionId}`,
            dto
        );
        return data.data!;
    },

    openSession: async (crewSlug: string, sessionId: string): Promise<PulseCheck> => {
        const { data } = await axiosService.post<ApiResponse<PulseCheck>>(
            `/c/${crewSlug}/pulse/sessions/${sessionId}/open`
        );
        return data.data!;
    },

    closeSession: async (crewSlug: string, sessionId: string): Promise<PulseCheck> => {
        const { data } = await axiosService.post<ApiResponse<PulseCheck>>(
            `/c/${crewSlug}/pulse/sessions/${sessionId}/close`
        );
        return data.data!;
    },

    deleteSession: async (crewSlug: string, sessionId: string): Promise<void> => {
        await axiosService.delete(`/c/${crewSlug}/pulse/sessions/${sessionId}`);
    },

    // Queue
    getQueue: async (crewSlug: string): Promise<PulseQueueItem[]> => {
        const { data } = await axiosService.get<ApiResponse<PulseQueueItem[]>>(
            `/c/${crewSlug}/pulse/queue`
        );
        return data.data ?? [];
    },

    // Respond
    getRespondView: async (crewSlug: string, token: string): Promise<PulseRespondView> => {
        const { data } = await axiosService.get<ApiResponse<PulseRespondView>>(
            `/c/${crewSlug}/pulse/respond/${token}`
        );
        return data.data!;
    },

    submitResponse: async (
        crewSlug: string,
        token: string,
        dto: SubmitPulseResponseDto
    ): Promise<void> => {
        await axiosService.post(`/c/${crewSlug}/pulse/respond/${token}`, dto);
    },

    // Insights
    getInsight: async (crewSlug: string, sessionId: string): Promise<PulseInsight | null> => {
        const { data } = await axiosService.get<ApiResponse<PulseInsight>>(
            `/c/${crewSlug}/pulse/sessions/${sessionId}/insight`
        );
        return data.data ?? null;
    },

    shareInsight: async (
        crewSlug: string,
        sessionId: string,
        dto: SharePulseInsightDto
    ): Promise<void> => {
        await axiosService.post(`/c/${crewSlug}/pulse/sessions/${sessionId}/insight/share`, dto);
    },

    revokeInsightShare: async (
        crewSlug: string,
        sessionId: string,
        profileId: string
    ): Promise<void> => {
        await axiosService.delete(
            `/c/${crewSlug}/pulse/sessions/${sessionId}/insight/share/${profileId}`
        );
    },

    regenerateInsight: async (crewSlug: string, sessionId: string): Promise<void> => {
        await axiosService.post(
            `/c/${crewSlug}/pulse/sessions/${sessionId}/insight/regenerate`
        );
    }
};

// ============================================
// Hooks
// ============================================

export function usePulseTemplates(crewSlug: string) {
    const pulseEnabled = useCrewModuleEnabled(crewSlug, 'pulseCheck');
    return useQuery({
        queryKey: pulseKeys.templates(crewSlug),
        queryFn: () => pulseApi.getTemplates(crewSlug),
        enabled: !!crewSlug && pulseEnabled
    });
}

export function usePulseChecks(crewSlug: string) {
    const pulseEnabled = useCrewModuleEnabled(crewSlug, 'pulseCheck');
    return useQuery({
        queryKey: pulseKeys.sessions(crewSlug),
        queryFn: () => pulseApi.getSessions(crewSlug),
        enabled: !!crewSlug && pulseEnabled
    });
}

export function usePulseCheck(crewSlug: string, sessionId: string) {
    return useQuery({
        queryKey: pulseKeys.session(crewSlug, sessionId),
        queryFn: () => pulseApi.getSession(crewSlug, sessionId),
        enabled: !!crewSlug && !!sessionId
    });
}

export function usePulseResponses(crewSlug: string, sessionId: string, enabled: boolean) {
    return useQuery({
        queryKey: pulseKeys.responses(crewSlug, sessionId),
        queryFn: () => pulseApi.getSessionResponses(crewSlug, sessionId),
        enabled: !!crewSlug && !!sessionId && enabled
    });
}

export function usePulseQueue(crewSlug: string) {
    const pulseEnabled = useCrewModuleEnabled(crewSlug, 'pulseCheck');
    return useQuery({
        queryKey: pulseKeys.queue(crewSlug),
        queryFn: () => pulseApi.getQueue(crewSlug),
        enabled: !!crewSlug && pulseEnabled,
        refetchInterval: 60_000 // refresh every minute
    });
}

export function usePulseRespondView(crewSlug: string, token: string) {
    return useQuery({
        queryKey: pulseKeys.respondView(crewSlug, token),
        queryFn: () => pulseApi.getRespondView(crewSlug, token),
        enabled: !!crewSlug && !!token,
        retry: false
    });
}

export function usePulseInsight(crewSlug: string, sessionId: string) {
    const session = useQuery({
        queryKey: pulseKeys.session(crewSlug, sessionId),
        queryFn: () => pulseApi.getSession(crewSlug, sessionId),
        enabled: !!crewSlug && !!sessionId
        // No polling — insight status updates arrive via SSE (useSse in CrewWrapper)
    });

    const insight = useQuery({
        queryKey: pulseKeys.insight(crewSlug, sessionId),
        queryFn: () => pulseApi.getInsight(crewSlug, sessionId),
        enabled: !!crewSlug && !!sessionId && session.data?.insightStatus === 'ready'
    });

    return { session, insight };
}

// ============================================
// Mutations
// ============================================

export function useCreatePulseCheck(crewSlug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: CreatePulseCheckDto) =>
            pulseApi.createSession(crewSlug, dto),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: pulseKeys.sessions(crewSlug) });
        }
    });
}

export function useUpdatePulseCheck(crewSlug: string, sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: UpdatePulseCheckDto) =>
            pulseApi.updateSession(crewSlug, sessionId, dto),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: pulseKeys.session(crewSlug, sessionId) });
        }
    });
}

export function useOpenPulseCheck(crewSlug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (sessionId: string) => pulseApi.openSession(crewSlug, sessionId),
        onSuccess: (_, sessionId) => {
            qc.invalidateQueries({ queryKey: pulseKeys.session(crewSlug, sessionId) });
            qc.invalidateQueries({ queryKey: pulseKeys.sessions(crewSlug) });
        }
    });
}

export function useClosePulseCheck(crewSlug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (sessionId: string) => pulseApi.closeSession(crewSlug, sessionId),
        onSuccess: (_, sessionId) => {
            qc.invalidateQueries({ queryKey: pulseKeys.session(crewSlug, sessionId) });
            qc.invalidateQueries({ queryKey: pulseKeys.sessions(crewSlug) });
        }
    });
}

export function useDeletePulseCheck(crewSlug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (sessionId: string) => pulseApi.deleteSession(crewSlug, sessionId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: pulseKeys.sessions(crewSlug) });
        }
    });
}

export function useSubmitPulseResponse(crewSlug: string, token: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: SubmitPulseResponseDto) =>
            pulseApi.submitResponse(crewSlug, token, dto),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: pulseKeys.queue(crewSlug) });
        }
    });
}

export function useSharePulseInsight(crewSlug: string, sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: SharePulseInsightDto) =>
            pulseApi.shareInsight(crewSlug, sessionId, dto),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: pulseKeys.insight(crewSlug, sessionId) });
        }
    });
}

export function useRevokePulseInsightShare(crewSlug: string, sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (profileId: string) =>
            pulseApi.revokeInsightShare(crewSlug, sessionId, profileId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: pulseKeys.insight(crewSlug, sessionId) });
        }
    });
}

export function useRegeneratePulseInsight(crewSlug: string, sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => pulseApi.regenerateInsight(crewSlug, sessionId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: pulseKeys.insight(crewSlug, sessionId) });
            qc.invalidateQueries({ queryKey: pulseKeys.session(crewSlug, sessionId) });
        }
    });
}

export function usePromotePulseTemplate(crewSlug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (templateId: string) => pulseApi.promoteTemplate(crewSlug, templateId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: pulseKeys.templates(crewSlug) });
        }
    });
}

export function useUpdatePulseTemplateHeaderImage(crewSlug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ templateId, headerImageUrl }: { templateId: string; headerImageUrl: string | null }) =>
            pulseApi.updateTemplateHeaderImage(crewSlug, templateId, headerImageUrl),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: pulseKeys.templates(crewSlug) });
        }
    });
}
